import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Fa from 'react-fontawesome';
import {
  DropdownButton,
  MenuItem,
  Button,
  Modal,
  Dropdown,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import tinycolor from 'tinycolor2';
import { indexOf, find, trim, reduce, map, reject } from 'lodash';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { translateMetaToFilter } from '../../util/dynamic-filter';
import DateRangeField from './date-range-field';
import StringField from './string-field';
import DropdownField from './drop-down-field';
import SliderField from './slider-field';
import BooleanField from './boolean-field';
import { addCriteria, deleteCriteria, updateCriteria } from './search-form-actions';
import SearchPath from '../search-path';
import ConfirmButton from '../confirm-button';
import { COLORS } from './master-field';
import ClickOutsideDetector from './click-outside-detector';
import SearchField from '../search-field';

import styles from './styles.css';

const MAX_LEVEL = 3;

const FadeAnimation = ({ children, ...props }) => (
  <CSSTransition
    {...props}
  >
    {children}
  </CSSTransition>
);
FadeAnimation.propTypes = {
  children: PropTypes.element,
};
FadeAnimation.defaultProps = {
  children: null,
};

class GroupField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      show: false,
      query: null,
      showModal: false,
    };
    this.addCriteria = this.addCriteria.bind(this);
    this.toggleLogic = this.toggleLogic.bind(this);
    this.deleteCriteria = this.deleteCriteria.bind(this);
    this.updateCriteria = this.updateCriteria.bind(this);
    this.deleteGroupCriteria = this.deleteGroupCriteria.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.hideModal = this.hideModal.bind(this);
    this.showModal = this.showModal.bind(this);
    this.handleSelectedSearchPath = this.handleSelectedSearchPath.bind(this);
  }

  getSubColor() {
    const { level } = this.props;
    return COLORS[(level + 1) % COLORS.length];
  }

  getFilters() {
    const {
      searchFilters, groupCriteria, level, systemFilters,
    } = this.props;
    const existentIds = map(groupCriteria.values || {}, '_id');
    const remainingFilters = reject(searchFilters, (searchFilter) => {
      searchFilter.valid_tag = indexOf(systemFilters, searchFilter.tag) >= 0;
      const exist = indexOf(existentIds, searchFilter._id) >= 0;
      return exist;
    });

    const canAddGroupFilter = level < MAX_LEVEL;
    const subColor = this.getSubColor();
    return {
      color: subColor,
      canAddGroupFilter,
      filters: remainingFilters,
    };
  }

  addCriteria(id) {
    const { groupCriteria, searchType } = this.props;
    this.props.dispatch(addCriteria(searchType, id, {}, groupCriteria));
    this.setState({ show: false, query: null });
    this.hideModal();
  }

  hideModal() {
    this.setState({ showModal: false }, () => {
      this.props.onModalHide();
    });
  }

  showModal() {
    this.setState({ showModal: true }, () => {
      this.props.onModalShow();
    });
  }

  updateCriteria(path, params) {
    const { searchType } = this.props;
    this.props.dispatch(updateCriteria(searchType, path, params));
  }

  toggleLogic(type) {
    const { path, searchType } = this.props;
    this.props.dispatch(updateCriteria(searchType, path, { type }));
  }

  deleteCriteria(path) {
    const { searchType } = this.props;
    this.props.dispatch(deleteCriteria(searchType, path));
  }

  deleteGroupCriteria() {
    const { searchType, path } = this.props;
    this.props.dispatch(deleteCriteria(searchType, path));
  }

  handleClickOutside() {
    this.setState({ show: false, query: null });
  }

  handleSearch(query) {
    this.setState({ query });
  }

  handleSelectedSearchPath(paths, item) {
    // create search node from paths info
    const { groupCriteria, searchType } = this.props;
    const dynamicFilterInfo = translateMetaToFilter(item, paths);
    this.props.dispatch(addCriteria(searchType, dynamicFilterInfo._id, {}, groupCriteria, dynamicFilterInfo));
    this.setState({ show: false, query: null });
    this.hideModal();
  }

  renderAddFilterModal() {
    const { disabled } = this.props;
    const { showModal } = this.state;
    const { filters, canAddGroupFilter, color } = this.getFilters();
    if (filters.length === 0) {
      return null;
    }
    return [
      (
        <Button key="btn" bsStyle="link" disabled={disabled} onClick={this.showModal}>
          <span style={{ color }}><Fa name="plus" /> Add Filter</span>
        </Button>
      ),
      (
        <Modal key="modal" bsSize="lg" show={showModal} onHide={this.hideModal}>
          <Modal.Header closeButton>
            <h4>Add Filter</h4>
          </Modal.Header>
          <Modal.Body>
            <div>
              <SearchField collapsible={false} onSearch={this.handleSearch} />
            </div>
            <hr />
            <div className={`${styles.addFilterBtn} open ${styles.addFilterModal}`}>
              <Dropdown.Menu
                onSelect={this.addCriteria}
              >
                {this.renderFilters(filters)}
              </Dropdown.Menu>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <hr />
            <div className="clearfix">
              {canAddGroupFilter &&
                <Button className="pull-left" disabled={disabled} bsStyle="link" onClick={() => { this.addCriteria(null); }}>
                  <Fa name="indent" /> Add Filter Group
                </Button>
              }
              <Button className="pull-right" bsStyle="default" onClick={this.hideModal}>Done</Button>
            </div>
          </Modal.Footer>
        </Modal>
      ),
    ];
  }

  renderFilters(filters, handler = null) {
    const { systemFilters } = this.props;
    const { query } = this.state;
    const isSearching = query && trim(query).length > 0;
    const menus = [];

    filters.forEach((remainingFilter) => {
      const isSystemFilter = remainingFilter.is_system_filter;
      const validTag = indexOf(systemFilters, remainingFilter.tag) >= 0;
      const disabledFilter = remainingFilter.disabled;
      let { label } = remainingFilter;
      let hasMatch = false;
      if (isSearching) {
        const matches = match(label, query);
        const parts = parse(label, matches).map((part, id) => ({ ...part, id }));
        label = reduce(parts, (items, part) => {
          const className = part.highlight ? styles.tagHighlight : null;
          if (part.highlight) {
            hasMatch = true;
          }
          items.push((<span className={className} key={part.id}>{part.text}</span>));
          return items;
        }, []);
      } else {
        hasMatch = true;
      }
      const animatedCls = isSearching ? 'animated flash' : '';
      const startCls = hasMatch ? 'text-warning' : 'text-muted';
      menus.push((
        <MenuItem
          onSelect={handler}
          disabled={(isSystemFilter && !validTag) || disabledFilter}
          key={remainingFilter._id}
          eventKey={remainingFilter._id}
        >
          <span className={(hasMatch ? animatedCls : 'text-muted')}>
            <Fa name={isSystemFilter ? 'star' : 'circle'} className={isSystemFilter ? startCls : 'text-muted'} />&nbsp;{label}
          </span>
        </MenuItem>
      ));
    });
    return menus;
  }

  renderAddFilterButton() {
    const { disabled, dropup } = this.props;
    const { show } = this.state;

    const icon = show ? 'minus' : 'plus';

    const { filters, canAddGroupFilter, color } = this.getFilters();

    return (
      <ClickOutsideDetector
        style={{ position: 'relative' }}
        className="pull-left"
        onClickOutSide={this.handleClickOutside}
      >
        <Button
          active={show}
          onClick={() => this.setState({ show: !show })}
          disabled={disabled}
          bsStyle="link"
        >
          <span style={{ color }}><Fa name={icon} /> Add Filter</span>
        </Button>
        <div className={`${styles.filterOverlay} ${(dropup ? styles.filterOverlayDropUp : '')} ${(show ? 'fade in' : 'fade out')}`}>
          {show &&
            <div className={`${styles.filterOverlaySearch} clearfix`}>
              <div className="pull-left">
                <SearchField collapsible={false} onSearch={this.handleSearch} />
              </div>
              <Button
                onClick={() => this.setState({ show: false, query: null })}
                className="pull-right"
                bsStyle="link"
              >
                <Fa name="times-circle" />
              </Button>
            </div>
          }
          <div className={`${styles.addFilterBtn} open`}>
            {show &&
              <ul className="dropdown-menu open">
                {this.renderFilters(filters, this.addCriteria)}
                {filters.length && canAddGroupFilter && <MenuItem divider />}
                {canAddGroupFilter &&
                  <MenuItem disabled={disabled} onClick={() => { this.addCriteria(null); }}>
                    <Fa name="indent" /> Add Filter Group
                  </MenuItem>
                }
              </ul>
            }
          </div>
        </div>
      </ClickOutsideDetector>
    );
  }

  renderLogicToggleBtn() {
    const {
      groupCriteria, disabled, disabledLogic,
      readOnly, isRoot, showRootLogicalORWarning,
    } = this.props;
    const logic = [
      { key: 'groupAND', label: 'AND' },
      { key: 'groupOR', label: 'OR' },
    ];
    const active = find(logic, { key: groupCriteria.type });
    const current = active ? active.key : groupCriteria.type;
    const displayComp = active !== undefined ? active.label : groupCriteria.type;
    if (readOnly) {
      let icon = null;
      if (groupCriteria.results != null) {
        const passed = groupCriteria.results === true;
        const tip = (
          <Tooltip id={`${groupCriteria._id}-passed-tip`}>{(passed ? 'Passed' : 'Failed')}</Tooltip>
        );
        icon = (
          <OverlayTrigger placement="top" overlay={tip}>
            <Fa name={passed ? 'check-circle' : 'exclamation-triangle'} className={passed ? 'text-success' : 'text-danger animated flash infinite'} />
          </OverlayTrigger>
        );
      }
      return (
        <div className={styles.logicContent}>
          <div className={styles.logicContentInner}>
            {displayComp} {icon}
          </div>
        </div>
      );
    }

    let warningIcon = null;
    if (showRootLogicalORWarning && isRoot && groupCriteria.type === 'groupOR') {
      const tipWarning = (
        <Tooltip id={`${groupCriteria._id}-warning-tip`}>Logical OR is active on root fitler set</Tooltip>
      );
      warningIcon = (
        <OverlayTrigger placement="top" overlay={tipWarning}>
          <Fa name="exclamation-triangle" className="text-danger animated flash infinite" />
        </OverlayTrigger>
      );
    }
    return (
      <div>
        <Button
          onClick={() => {
            this.toggleLogic(current === 'groupAND' ? 'groupOR' : 'groupAND');
          }}
          disabled={disabled || disabledLogic}
          bsStyle="link"
        >
          {displayComp} {warningIcon}
        </Button>
      </div>
    );
  }

  renderAddDynamicFilterButton() {
    const { disabled, dropup, startingNode } = this.props;
    const { showModal } = this.state;
    const { color } = this.getFilters();
    const selectedSearchPaths = [];
    return [
      (
        <DropdownButton
          key="add-dynamic-filter-dropdown"
          noCaret
          dropup={dropup}
          disabled={disabled}
          bsStyle="link"
          title={<span style={{ color }}><Fa name="plus" /> Add Filter</span>}
          id="add-dynamic-filter"
        >
          <MenuItem disabled={disabled} onClick={this.showModal}>
            <Fa name="plus" /> Add Filter
          </MenuItem>
          <MenuItem divider />
          <MenuItem disabled={disabled} onClick={() => { this.addCriteria(null); }}>
            <Fa name="indent" /> Add Filter Group
          </MenuItem>
        </DropdownButton>
      ),
      (
        <Modal key="dynamic-filter-modal" bsSize="lg" show={showModal} onHide={this.hideModal}>
          <Modal.Header closeButton>
            <h4>Add Filter</h4>
          </Modal.Header>
          <Modal.Body>
            <div>
              {showModal &&
                <SearchPath
                  paths={selectedSearchPaths}
                  disabled={disabled}
                  startingNode={startingNode}
                  onUpdate={this.handleSelectedSearchPath}
                  selectedSearchPaths={selectedSearchPaths}
                  checkFieldType
                />
              }
            </div>
          </Modal.Body>
          <Modal.Footer>
            <hr />
            <div className="clearfix">
              <Button className="pull-right" bsStyle="default" onClick={this.hideModal}>Done</Button>
            </div>
          </Modal.Footer>
        </Modal>
      ),
    ]
  }

  renderCustomFooterControls() {
    if (this.props.customFooterControls) {
      return this.props.customFooterControls;
    }
    return null;
  }

  render() {
    const {
      searchFilters, groupCriteria, systemFilters, overrideFields,
      disabled, level, isRoot, path, children,
      searchType, dropup, modalModel, readOnly, showInclude,
      isDynamic, startingNode, className, 
    } = this.props;
    const fields = groupCriteria.values || [];
    const hasChildGroup = find(fields, f => f.type === 'groupAND' || f.type === 'groupOR') !== undefined;
    const cls = className ? [className.split(' ')] : [styles.field, styles.fieldGroup];

    if (hasChildGroup) {
      cls.push(styles.fieldHasChildGroup);
    }
    if (isRoot) {
      cls.push(styles.fieldGroupRoot);
    }
    const color = COLORS[level % COLORS.length];
    const borderStyle = { borderColor: color };
    if (this.props.color) {
      borderStyle.backgroundColor = tinycolor.mix(this.props.color, '#ffffff', 95).toHexString();
    }
    const subColor = this.getSubColor();

    if (isRoot && fields.length === 0) {
      if (readOnly) {
        return null;
      }
      return (
        <div className={`${styles.fieldGroupFooter} clearfix`}>
          {!isDynamic && this.renderAddFilterModal()}
          {isDynamic && (<div style={{ marginTop: '45px' }}>
            {this.renderAddDynamicFilterButton()}
          </div>)}
          {this.renderCustomFooterControls()}
        </div>
      );
    }
    const hasChildren = children !== undefined && children !== null;
    return (
      <div style={borderStyle} className={`${cls.join(' ')}`}>
        <div className={styles.fieldGroupContent}>
          {fields.length > 1 &&
            <div className={styles.fieldGroupLogic}>
              <div className={styles.fieldGroupLogicInner} style={{ background: subColor }}>
                {this.renderLogicToggleBtn()}
              </div>
            </div>
          }
          <div className={styles.fieldGroupFields}>
            <TransitionGroup className={((hasChildren || fields.length > 0) ? styles.fieldGroupBody : '')}>
              {hasChildren && children}
              {fields.map((searchCriteria) => {
                const isGroup = searchCriteria.type === 'groupAND' || searchCriteria.type === 'groupOR';
                let FieldComponent = null;
                let attrs = {
                  onModalShow: this.props.onModalShow,
                  onModalHide: this.props.onModalHide,
                  level: level + 1,
                  hasSiblingGroup: hasChildGroup,
                  path: [...path, searchCriteria._id],
                  searchType,
                  onUpdate: this.updateCriteria,
                  onDelete: this.deleteCriteria,
                  dropdownRef: this.props.dropdownRef,
                  readOnly,
                  isDynamic,
                  startingNode,
                };

                if (!isGroup) {
                  const isSystemFilter = searchCriteria.filter.is_system_filter;
                  const validTag = indexOf(systemFilters, searchCriteria.filter.tag) >= 0;
                  const fieldDisabled = isSystemFilter && !validTag;
                  FieldComponent = DropdownField;
                  if (searchCriteria.type === 'set') {
                    FieldComponent = BooleanField;
                  } else if (searchCriteria.filter.type === 'daterange') {
                    FieldComponent = DateRangeField;
                  } else if (searchCriteria.filter.type === 'range') {
                    FieldComponent = SliderField;
                  } else if (searchCriteria.filter.is_boolean) {
                    FieldComponent = BooleanField;
                  } else if (searchCriteria.filter.filter_id === 'Gong__Gong_Call__c/Gong__Opp_Stage_Time_Of_Call__c') {
                    FieldComponent = DropdownField;
                  } else if (searchCriteria.filter.$fieldMetaType === 'string' || searchCriteria.filter.$fieldMetaType === 'email' || searchCriteria.filter.$fieldMetaType === 'textarea') {
                    FieldComponent = StringField;
                  }
                  if (overrideFields[searchCriteria.filter.tag]) {
                    FieldComponent = overrideFields[searchCriteria.filter.tag].component || FieldComponent;
                    attrs = { ...attrs, ...overrideFields[searchCriteria.filter.tag].attributes };
                  }
                  attrs.disabled = disabled || (!isDynamic && fieldDisabled);
                  attrs.searchCriteria = searchCriteria;
                  attrs.color = subColor;
                } else {
                  FieldComponent = ConnectedGroupField;  // eslint-disable-line
                  attrs.searchFilters = searchFilters;
                  attrs.systemFilters = systemFilters;
                  attrs.overrideFields = overrideFields;
                  attrs.disabled = disabled;
                  attrs.groupCriteria = searchCriteria;
                  attrs.dropup = dropup;
                  attrs.modalModel = true;
                  attrs.color = subColor;
                  attrs.showInclude = showInclude;
                }

                return (
                  <FadeAnimation
                    classNames="field"
                    timeout={800}
                    key={searchCriteria._id}
                  >
                    <FieldComponent {...attrs} />
                  </FadeAnimation>
                );
              })}
            </TransitionGroup>
          </div>
        </div>
        {!readOnly &&
          <div className={`${styles.fieldGroupFooter} clearfix`}>
            {!modalModel && !isDynamic && this.renderAddFilterButton()}
            {!modalModel && isDynamic && this.renderAddDynamicFilterButton()}
            {modalModel && !isDynamic && this.renderAddFilterModal()}
            {modalModel && isDynamic && this.renderAddDynamicFilterButton()}
            {this.renderCustomFooterControls()}
            {!isRoot &&
              <div className="pull-right" style={{ paddingRight: '10px' }}>
                <ConfirmButton
                  disabled={disabled}
                  confirmMessage="?"
                  confirmStyle="link"
                  onConfirm={this.deleteGroupCriteria}
                >
                  <span style={{ color: subColor }}><Fa name="times-circle" /> Remove Group</span>
                </ConfirmButton>
              </div>
            }
          </div>
        }
      </div>
    );
  }
}

GroupField.propTypes = {
  dispatch: PropTypes.func.isRequired,
  groupCriteria: PropTypes.shape({
    _id: PropTypes.string,
    type: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.shape({})),
    results: PropTypes.bool,
  }).isRequired,
  path: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
  showInclude: PropTypes.bool,
  searchFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  systemFilters: PropTypes.arrayOf(PropTypes.string),
  overrideFields: PropTypes.objectOf(PropTypes.shape({
    attributes: PropTypes.object,
    component: PropTypes.func, // { stage: <StageComponent> }
  })),
  onModalShow: PropTypes.func,
  onModalHide: PropTypes.func,
  disabledLogic: PropTypes.bool,
  isRoot: PropTypes.bool,
  level: PropTypes.number,
  children: PropTypes.node,
  type: PropTypes.string,
  searchType: PropTypes.string.isRequired,
  customFooterControls: PropTypes.node,
  dropup: PropTypes.bool,
  modalModel: PropTypes.bool,
  color: PropTypes.string,
  dropdownRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.elementType }),
  ]),
  readOnly: PropTypes.bool,
  isDynamic: PropTypes.bool,
  startingNode: PropTypes.string,
  className: PropTypes.string,
  showRootLogicalORWarning: PropTypes.bool,
};
GroupField.defaultProps = {
  systemFilters: [],
  overrideFields: {},
  disabled: false,
  isRoot: false,
  disabledLogic: false,
  showInclude: true,
  onModalShow: () => {},
  onModalHide: () => {},
  level: 0,
  children: null,
  customFooterControls: null,
  dropup: false,
  modalModel: false,
  type: 'groupAND',
  color: null,
  dropdownRef: null,
  readOnly: false,
  isDynamic: false,
  startingNode: 'Opportunity',
  className: null,
  showRootLogicalORWarning: false,
};
GroupField.contextTypes = {
};

// Retrieve data from store as props
function mapStateToProps() {
  return {};
}

const ConnectedGroupField = connect(mapStateToProps)(GroupField);
export default ConnectedGroupField;
